/**
 * Subdomain detection utilities for routing different landing pages
 *
 * - cyberlete.net -> Main gaming landing page
 * - cloud.cyberlete.net -> DePIN landing page
 * - gameservers.cyberlete.net -> Game Servers landing page
 */

/**
 * Get the current subdomain from the hostname
 * Returns null if no subdomain (e.g., cyberlete.net, localhost)
 */
export const getSubdomain = (): string | null => {
  const hostname = window.location.hostname

  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for subdomain simulation via query param for local testing
    const params = new URLSearchParams(window.location.search)
    const simulatedSubdomain = params.get('subdomain')
    if (simulatedSubdomain) {
      return simulatedSubdomain
    }
    return null
  }

  // Split hostname into parts
  const parts = hostname.split('.')

  // cyberlete.net = 2 parts (no subdomain)
  // cloud.cyberlete.net = 3 parts (has subdomain)
  // www.cyberlete.net = 3 parts (www subdomain)
  if (parts.length > 2) {
    const subdomain = parts[0]
    // Ignore 'www' as it's not a real subdomain for our purposes
    if (subdomain === 'www') {
      return null
    }
    return subdomain
  }

  return null
}

/**
 * Check if we're on the cloud subdomain (cloud.cyberlete.net)
 * This subdomain serves the DePIN landing page
 */
export const isCloudSubdomain = (): boolean => {
  return getSubdomain() === 'cloud'
}

/**
 * Check if we're on the gameservers subdomain (gameservers.cyberlete.net)
 * This subdomain serves the Game Servers landing page
 */
export const isGameServersSubdomain = (): boolean => {
  return getSubdomain() === 'gameservers'
}

/**
 * Check if we're on any subdomain (not the main domain)
 */
export const isSubdomain = (): boolean => {
  return getSubdomain() !== null
}

/**
 * Check if we're on the main domain (cyberlete.net or www.cyberlete.net)
 */
export const isMainDomain = (): boolean => {
  const subdomain = getSubdomain()
  return subdomain === null
}

/**
 * Get the base URL for the main app
 * Used for links from cloud.cyberlete.net to cyberlete.net
 */
export const getMainAppUrl = (): string => {
  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${window.location.port}`
  }

  // Production - always point to main domain
  return `${protocol}//cyberlete.net`
}

/**
 * Get the base URL for the cloud subdomain (DePIN)
 * Used for links from cyberlete.net to cloud.cyberlete.net
 */
export const getCloudUrl = (): string => {
  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Local development - use query param for testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${window.location.port}?subdomain=cloud`
  }

  // Production
  return `${protocol}//cloud.cyberlete.net`
}

/**
 * Get the base URL for the gameservers subdomain
 * Used for links from cyberlete.net to gameservers.cyberlete.net
 */
export const getGameServersUrl = (): string => {
  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Local development - use query param for testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${window.location.port}?subdomain=gameservers`
  }

  // Production
  return `${protocol}//gameservers.cyberlete.net`
}
